import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Stack,
  Chip,
  Box,
  Tooltip,
} from '@mui/material';
import {
  Tag as CategoryIcon,
  MapPin as LocationIcon,
  WrenchIcon,
  Power,
  Euro,
  AlertTriangle,
  Image as ImageIcon,
  Navigation as SecondaryLocationIcon,
  ChevronDown,
  Trash2,
} from 'lucide-react';
import type { InventoryItem } from '../types/inventoryTypes';

// Category color mappings
const CATEGORY_COLORS: Record<string, { color: string; background: string }> = {
  Sound: { color: '#2563eb', background: '#dbeafe' },        // Blue
  Lighting: { color: '#7c3aed', background: '#ede9fe' },     // Purple
  Video: { color: '#be185d', background: '#fce7f3' },        // Pink
  Power: { color: '#b45309', background: '#fef3c7' },        // Amber
  Office: { color: '#047857', background: '#d1fae5' },       // Emerald
  Stage: { color: '#4f46e5', background: '#e0e7ff' },        // Indigo
  Cables: { color: '#dc2626', background: '#fee2e2' },       // Red
  Cases: { color: '#0369a1', background: '#e0f2fe' },        // Light Blue
  Microphones: { color: '#059669', background: '#ecfdf5' },  // Green
  Cameras: { color: '#9333ea', background: '#f3e8ff' },      // Violet
  Other: { color: '#374151', background: '#f4f4f5' },         // Gray
  FOH: { color: '#d97706', background: '#fffaf0' },          // Orange
  Rigging: { color: '#dc2626', background: '#fee2e2' },      // Red
};

interface ItemCardProps {
  item: InventoryItem;
  onClick: () => void;
  onMaintenanceClick: (e: React.MouseEvent) => void;
  onDelete?: (id: string) => void;
  onCategoryFilter?: (category: string) => void;
  onLocationFilter?: (location: string) => void;
  onShelfFilter?: (location: string, shelf: string) => void;
}

export const ItemCard: React.FC<ItemCardProps> = ({
  item,
  onClick,
  onMaintenanceClick,
  onDelete,
  onCategoryFilter,
  onLocationFilter,
  onShelfFilter,
}) => {
  const getMaintenanceColor = (): "success" | "warning" | "error" => {
    switch (item.maintenanceStatus) {
      case 'ok':
        return 'success';
      case 'due':
        return 'warning';
      case 'overdue':
        return 'error';
      default:
        return 'success';
    }
  };

  const renderImageSection = () => {
    if (!item.images?.length) {
      return (
        <Box
          sx={{
            height: 140,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
          }}
        >
          <ImageIcon size={40} color="gray" />
        </Box>
      );
    }
  
    const imageUrl = item.images[0].url;
  
    return (
      <Box
        sx={{
          height: 140,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.1)',
            opacity: 0,
            transition: 'opacity 0.2s',
          },
          '&:hover::before': {
            opacity: 1,
          },
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt={item.name}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
          }}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const parent = target.parentElement;
            if (parent) {
              parent.style.backgroundColor = 'action.hover';
              parent.innerHTML = '<div style="display: flex; justify-content: center; align-items: center; height: 100%;"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="gray" stroke-width="2"><path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/><path d="M9 10h.01M15 10h.01M9.5 15.5c1.333-1 3.667-1 5 0"/></svg></div>';
            }
          }}
        />
      </Box>
    );
  };

  const renderLocationInfo = () => {
    const locations = [];

    // Primary location (always show)
    locations.push(
      <Tooltip 
        key="primary"
        title="Click to filter by this location"
      >
        <Chip
          size="small"
          icon={<LocationIcon size={14} />}
          label={item.primaryLocation}
          clickable
          onClick={(e) => {
            e.stopPropagation();
            onLocationFilter?.(item.primaryLocation);
          }}
          sx={{ 
            fontWeight: 500,
            cursor: 'pointer',
            '&:hover': {
              boxShadow: 2,
              transform: 'scale(1.02)',
            },
            transition: 'all 0.2s ease-in-out',
            '& .MuiChip-icon': { 
              color: 'inherit' 
            }
          }}
        />
      </Tooltip>
    );

    // Second location (shelf/drawer)
    if (item.secondLocation) {
      locations.push(
        <Tooltip 
          key="second"
          title={`Click to filter by ${item.primaryLocation} - ${item.secondLocation}`}
        >
          <Chip
            size="small"
            icon={<ChevronDown size={14} />}
            label={item.secondLocation}
            variant="outlined"
            clickable
            onClick={(e) => {
              e.stopPropagation();
              onShelfFilter?.(item.primaryLocation, item.secondLocation!);
            }}
            sx={{ 
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 2,
                transform: 'scale(1.02)',
              },
              transition: 'all 0.2s ease-in-out',
              '& .MuiChip-icon': { 
                color: 'inherit'
              }
            }}
          />
        </Tooltip>
      );
    }

    // Secondary location (alternative location)
    if (item.secondaryLocation && item.secondaryLocation !== 'N/A') {
      locations.push(
        <Tooltip 
          key="secondary"
          title="Alternative Location"
        >
          <Chip
            size="small"
            icon={<SecondaryLocationIcon size={14} />}
            label={item.secondaryLocation}
            variant="outlined"
            color="info"
            sx={{ 
              '& .MuiChip-icon': { 
                color: 'inherit'
              }
            }}
          />
        </Tooltip>
      );
    }

    return (
      <Stack 
        direction="row" 
        spacing={1} 
        sx={{ 
          mb: 2,
          flexWrap: 'wrap',
          gap: 0.5
        }}
      >
        <Tooltip title="Click to filter by this category">
          <Chip
            size="small"
            icon={<CategoryIcon size={14} />}
            label={item.category}
            clickable
            onClick={(e) => {
              e.stopPropagation();
              onCategoryFilter?.(item.category);
            }}
            sx={{
              color: CATEGORY_COLORS[item.category]?.color || 'text.primary',
              bgcolor: CATEGORY_COLORS[item.category]?.background || 'default',
              fontWeight: 500,
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 2,
                transform: 'scale(1.02)',
              },
              transition: 'all 0.2s ease-in-out',
              '& .MuiChip-icon': {
                color: 'inherit'
              }
            }}
          />
        </Tooltip>
        {locations}
      </Stack>
    );
  };

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete && window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
      onDelete(item._id);
    }
  };

  // Truncate text function for better display
  const truncateText = (text: string, maxLength: number = 45) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

  return (
    <Card 
      onClick={onClick}
      sx={{ 
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)',
        },
        transition: 'all 0.2s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
        height: '100%', // Make cards same height
      }}
    >
      {renderImageSection()}
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header with Title, Quantity, and Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          mb: 2
        }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}> {/* minWidth: 0 allows text to shrink */}
            <Tooltip title={item.name}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 1,
                  wordWrap: 'break-word',
                  lineHeight: 1.2,
                  // Multi-line text with ellipsis after 2 lines
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {item.name}
              </Typography>
            </Tooltip>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 'bold',
                color: 'primary.main',
                lineHeight: 1
              }}
            >
              {item.quantity}
              <Typography 
                component="span" 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  ml: 1
                }}
              >
                units
              </Typography>
            </Typography>
          </Box>
          
          {/* Action Buttons */}
          <Stack direction="row" spacing={0.5} sx={{ ml: 1 }}>
            <Tooltip title="Maintenance">
              <IconButton
                size="small"
                onClick={(e) => {
                  stopPropagation(e);
                  onMaintenanceClick(e);
                }}
                color={getMaintenanceColor()}
              >
                <WrenchIcon size={18} />
              </IconButton>
            </Tooltip>
            
            {onDelete && (
              <Tooltip title="Delete Item">
                <IconButton
                  size="small"
                  onClick={handleDelete}
                  color="error"
                  sx={{
                    '&:hover': {
                      backgroundColor: 'error.light',
                      color: 'error.contrastText',
                    }
                  }}
                >
                  <Trash2 size={18} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>

        {/* Category and All Locations */}
        {renderLocationInfo()}

        {/* Status Chips */}
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
          {item.requiresPower && (
            <Chip
              size="small"
              icon={<Power size={14} />}
              label="Requires Power"
              color="warning"
              sx={{ 
                '& .MuiChip-icon': { 
                  color: 'inherit'
                }
              }}
            />
          )}
          {item.isInsured && (
            <Chip
              size="small"
              icon={<Euro size={14} />}
              label="Insured"
              color="success"
              sx={{ 
                '& .MuiChip-icon': { 
                  color: 'inherit'
                }
              }}
            />
          )}
          {item.maintenanceStatus === 'overdue' && (
            <Chip
              size="small"
              icon={<AlertTriangle size={14} />}
              label="Maintenance Overdue"
              color="error"
              sx={{ 
                '& .MuiChip-icon': { 
                  color: 'inherit'
                }
              }}
            />
          )}
        </Stack>

        {/* Additional Info - Push to bottom */}
        <Box sx={{ mt: 'auto' }}>
          {item.serialNumber && (
            <Typography 
              variant="caption" 
              display="block" 
              color="text.secondary"
              sx={{ 
                wordBreak: 'break-word',
                lineHeight: 1.3
              }}
            >
              S/N: {truncateText(item.serialNumber, 25)}
            </Typography>
          )}
          
          {item.currentValue != null && item.currentValue > 0 && (
            <Typography variant="caption" display="block" color="text.secondary">
              Current Value: €{item.currentValue.toLocaleString()}
            </Typography>
          )}

          {item.lastUsedBy && (
            <Typography 
              variant="caption" 
              display="block" 
              color="text.secondary"
              sx={{ 
                mt: 1,
                lineHeight: 1.3
              }}
            >
              Last used by {truncateText(item.lastUsedBy.userName, 15)} on{' '}
              {new Date(item.lastUsedBy.timestamp).toLocaleDateString()}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ItemCard;